import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import UpperCaseInput from '../../../components/UpperCaseInput';
import { CORREDOR_OPTIONS } from '../utils/constants';
import { kgToTons, tonsToKg } from '../utils/helpers';
import { toast } from 'sonner';

const MAX_TONS = 35;

const TruckDataModal = ({
  isOpen,
  onClose,
  selectedTruck,
  truckData,
  setTruckData,
  isModification,
  justification,
  setJustification,
  companies,
  filteredTrucks,
  filteredTrailers,
  filteredDrivers,
  businesses,
  compras,
  onSubmit
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    const cargadas = parseFloat(kgToTons(truckData.kg_loaded)) || 0;
    const descargadas = parseFloat(kgToTons(truckData.kg_unloaded)) || 0;
    if (cargadas > MAX_TONS) {
      toast.error(`Las toneladas cargadas no pueden superar ${MAX_TONS} T (ingresado: ${cargadas} T)`);
      return;
    }
    if (descargadas > MAX_TONS) {
      toast.error(`Las toneladas descargadas no pueden superar ${MAX_TONS} T (ingresado: ${descargadas} T)`);
      return;
    }
    onSubmit(e);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>
            {isModification ? 'Modificar Datos' : 'Agregar Datos'} - Línea #{selectedTruck?.order}
          </DialogTitle>
        </DialogHeader>
        
        {isModification ? (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800">
            <strong>Nota:</strong> Estás modificando datos existentes. Se requiere justificación. Si cambias el correlativo, la lista se reordenará automáticamente.
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-xs text-emerald-800">
            <strong>Nota:</strong> Estás completando datos vacíos. No se requiere justificación.
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Selectores de negocio y compra - siempre visibles */}
          <div className="grid grid-cols-2 gap-3">
            {/* Selector de negocio (para descarga) */}
            <div className="border border-blue-100 rounded-md p-3 bg-blue-50">
              <Label className="text-xs font-semibold text-blue-700 uppercase mb-1 block">Negocio (Descarga) <span className="text-blue-400 font-normal">(opc.)</span></Label>
              <Select
                value={truckData.negocio_id || "none"}
                onValueChange={(v) => setTruckData({ ...truckData, negocio_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sin negocio vinculado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin negocio</SelectItem>
                  {(businesses || []).filter(b => b.status === 'activo').map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      <span className="font-mono font-semibold">{b.business_number}</span>
                      {' · '}{b.client_name || '—'}
                      {' · '}{b.producto}
                      {b.cierre ? ` · #${b.cierre}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Selector de compra (para carga) */}
            <div className="border border-emerald-100 rounded-md p-3 bg-emerald-50">
              <Label className="text-xs font-semibold text-emerald-700 uppercase mb-1 block">Compra (Carga) <span className="text-emerald-400 font-normal">(opc.)</span></Label>
              <Select
                value={truckData.compra_id || "none"}
                onValueChange={(v) => setTruckData({ ...truckData, compra_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sin compra vinculada" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin compra</SelectItem>
                  {(compras || []).filter(c => (c.kg_disponibles || c.cantidad_kg) > 0).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono font-semibold">{c.tipo_grano}</span>
                      {' · '}{c.proveedor_name || '—'}
                      {' · Disp: '}{((c.kg_disponibles ?? c.cantidad_kg) / 1000).toFixed(1)}T
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* MODO MODIFICAR: Solo muestra campos CON datos */}
          {isModification && (
            <>
              {/* Sección: Datos de Transporte */}
              {(selectedTruck?.load_date || selectedTruck?.delivery_date || selectedTruck?.broker || selectedTruck?.freight_value) && (
                <div className="border-b pb-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Transporte</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedTruck?.load_date && (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha Carga</Label>
                        <Input type="date" value={truckData.load_date} onChange={(e) => setTruckData({ ...truckData, load_date: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {selectedTruck?.delivery_date && (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha Descarga</Label>
                        <Input type="date" value={truckData.delivery_date} onChange={(e) => setTruckData({ ...truckData, delivery_date: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {selectedTruck?.broker && (
                      <div className="space-y-1">
                        <Label className="text-xs">Corredor</Label>
                        <Select value={truckData.broker} onValueChange={(v) => setTruckData({ ...truckData, broker: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>{CORREDOR_OPTIONS.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {selectedTruck?.freight_value > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Precio Flete</Label>
                        <Input type="number" step="0.01" value={truckData.freight_value} onChange={(e) => setTruckData({ ...truckData, freight_value: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sección: Datos de identificación - siempre visible en modificar */}
              <div className="border-b pb-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Identificación del Camión</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Correlativo</Label>
                    <Input type="number" min="1" value={truckData.order} onChange={(e) => setTruckData({ ...truckData, order: parseInt(e.target.value) || 1 })} className="h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Empresa</Label>
                    <Select value={truckData.company_id} onValueChange={(v) => setTruckData({ ...truckData, company_id: v, truck_id: '', trailer_id: '', driver_id: '' })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.alias || c.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Patente Camión</Label>
                    <Select value={truckData.truck_id} onValueChange={(v) => setTruckData({ ...truckData, truck_id: v })} disabled={!truckData.company_id}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{filteredTrucks.map(t => (<SelectItem key={t.id} value={t.id}>{t.license_plate}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Patente Acoplado</Label>
                    <Select value={truckData.trailer_id || "none"} onValueChange={(v) => setTruckData({ ...truckData, trailer_id: v === "none" ? "" : v })} disabled={!truckData.company_id}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin acoplado</SelectItem>
                        {filteredTrailers.map(t => (<SelectItem key={t.id} value={t.id}>{t.license_plate}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Chofer</Label>
                    <Select value={truckData.driver_id} onValueChange={(v) => setTruckData({ ...truckData, driver_id: v })} disabled={!truckData.company_id}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{filteredDrivers.map(d => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Datos de Carga - SIEMPRE visible en modo modificar */}
              <div className="border-b pb-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Carga</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Ton. Cargadas <span className="text-slate-400">(máx. {MAX_TONS} T)</span></Label>
                    <Input type="number" step="0.001" min="0" max={MAX_TONS} value={kgToTons(truckData.kg_loaded)} onChange={(e) => setTruckData({ ...truckData, kg_loaded: tonsToKg(e.target.value) })} className={`h-8 text-xs ${parseFloat(kgToTons(truckData.kg_loaded)) > MAX_TONS ? 'border-red-500 focus-visible:ring-red-500' : ''}`} placeholder="0,000" />
                    {parseFloat(kgToTons(truckData.kg_loaded)) > MAX_TONS && <p className="text-red-500 text-xs">Máximo {MAX_TONS} T</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ton. Descargadas <span className="text-slate-400">(máx. {MAX_TONS} T)</span></Label>
                    <Input type="number" step="0.001" min="0" max={MAX_TONS} value={kgToTons(truckData.kg_unloaded)} onChange={(e) => setTruckData({ ...truckData, kg_unloaded: tonsToKg(e.target.value) })} className={`h-8 text-xs ${parseFloat(kgToTons(truckData.kg_unloaded)) > MAX_TONS ? 'border-red-500 focus-visible:ring-red-500' : ''}`} placeholder="0,000" />
                    {parseFloat(kgToTons(truckData.kg_unloaded)) > MAX_TONS && <p className="text-red-500 text-xs">Máximo {MAX_TONS} T</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Destino</Label>
                    <UpperCaseInput value={truckData.destination} onChange={(e) => setTruckData({ ...truckData, destination: e.target.value })} className="h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Comuna</Label>
                    <UpperCaseInput value={truckData.commune} onChange={(e) => setTruckData({ ...truckData, commune: e.target.value })} className="h-8 text-xs" />
                  </div>
                </div>
              </div>
              
              {/* Datos de Venta y Transporte - solo campos con datos */}
              {(selectedTruck?.sale_price || selectedTruck?.sale_invoice || selectedTruck?.transport_invoice || selectedTruck?.guide_number) && (
                <div className="border-b pb-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Venta y Transporte</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedTruck?.sale_price > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Precio Venta</Label>
                        <Input type="number" step="0.01" value={truckData.sale_price} onChange={(e) => setTruckData({ ...truckData, sale_price: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" />
                      </div>
                    )}
                    {selectedTruck?.sale_invoice && (
                      <div className="space-y-1">
                        <Label className="text-xs">Factura Venta</Label>
                        <UpperCaseInput value={truckData.sale_invoice} onChange={(e) => setTruckData({ ...truckData, sale_invoice: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {selectedTruck?.transport_invoice && (
                      <div className="space-y-1">
                        <Label className="text-xs">Factura Transporte</Label>
                        <UpperCaseInput value={truckData.transport_invoice} onChange={(e) => setTruckData({ ...truckData, transport_invoice: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {selectedTruck?.guide_number && (
                      <div className="space-y-1">
                        <Label className="text-xs">N° Guía</Label>
                        <UpperCaseInput value={truckData.guide_number} onChange={(e) => setTruckData({ ...truckData, guide_number: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-1 pt-2 border-t">
                <Label className="text-xs text-red-600">Justificación *</Label>
                <UpperCaseInput value={justification} onChange={(e) => setJustification(e.target.value)} required placeholder="Motivo de la modificación" className="h-8 text-xs" />
              </div>
            </>
          )}

          {/* MODO AGREGAR: Solo muestra campos SIN datos */}
          {!isModification && (
            <>
              {/* Datos de Transporte - solo campos vacíos */}
              {(!selectedTruck?.load_date || !selectedTruck?.delivery_date || !selectedTruck?.broker || !selectedTruck?.freight_value) && (
                <div className="border-b pb-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Transporte</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {!selectedTruck?.load_date && (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha Carga</Label>
                        <Input type="date" value={truckData.load_date} onChange={(e) => setTruckData({ ...truckData, load_date: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.delivery_date && (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha Descarga</Label>
                        <Input type="date" value={truckData.delivery_date} onChange={(e) => setTruckData({ ...truckData, delivery_date: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.broker && (
                      <div className="space-y-1">
                        <Label className="text-xs">Corredor</Label>
                        <Select value={truckData.broker} onValueChange={(v) => setTruckData({ ...truckData, broker: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>{CORREDOR_OPTIONS.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {!selectedTruck?.freight_value && (
                      <div className="space-y-1">
                        <Label className="text-xs">Precio Flete</Label>
                        <Input type="number" step="0.01" value={truckData.freight_value} onChange={(e) => setTruckData({ ...truckData, freight_value: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Datos de Carga - solo campos vacíos */}
              {(!selectedTruck?.kg_loaded || !selectedTruck?.kg_unloaded || !selectedTruck?.destination || !selectedTruck?.commune) && (
                <div className="border-b pb-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Carga</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {!selectedTruck?.kg_loaded && (
                      <div className="space-y-1">
                        <Label className="text-xs">Ton. Cargadas <span className="text-slate-400">(máx. {MAX_TONS} T)</span></Label>
                        <Input type="number" step="0.001" min="0" max={MAX_TONS} value={kgToTons(truckData.kg_loaded)} onChange={(e) => setTruckData({ ...truckData, kg_loaded: tonsToKg(e.target.value) })} className={`h-8 text-xs ${parseFloat(kgToTons(truckData.kg_loaded)) > MAX_TONS ? 'border-red-500 focus-visible:ring-red-500' : ''}`} placeholder="0,000" />
                        {parseFloat(kgToTons(truckData.kg_loaded)) > MAX_TONS && <p className="text-red-500 text-xs">Máximo {MAX_TONS} T</p>}
                      </div>
                    )}
                    {!selectedTruck?.kg_unloaded && (
                      <div className="space-y-1">
                        <Label className="text-xs">Ton. Descargadas <span className="text-slate-400">(máx. {MAX_TONS} T)</span></Label>
                        <Input type="number" step="0.001" min="0" max={MAX_TONS} value={kgToTons(truckData.kg_unloaded)} onChange={(e) => setTruckData({ ...truckData, kg_unloaded: tonsToKg(e.target.value) })} className={`h-8 text-xs ${parseFloat(kgToTons(truckData.kg_unloaded)) > MAX_TONS ? 'border-red-500 focus-visible:ring-red-500' : ''}`} placeholder="0,000" />
                        {parseFloat(kgToTons(truckData.kg_unloaded)) > MAX_TONS && <p className="text-red-500 text-xs">Máximo {MAX_TONS} T</p>}
                      </div>
                    )}
                    {!selectedTruck?.destination && (
                      <div className="space-y-1">
                        <Label className="text-xs">Destino</Label>
                        <UpperCaseInput value={truckData.destination} onChange={(e) => setTruckData({ ...truckData, destination: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.commune && (
                      <div className="space-y-1">
                        <Label className="text-xs">Comuna</Label>
                        <UpperCaseInput value={truckData.commune} onChange={(e) => setTruckData({ ...truckData, commune: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Datos de Venta y Transporte - solo campos vacíos */}
              {(!selectedTruck?.sale_price || !selectedTruck?.sale_invoice || !selectedTruck?.transport_invoice || !selectedTruck?.guide_number) && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Datos de Venta y Transporte</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {!selectedTruck?.sale_price && (
                      <div className="space-y-1">
                        <Label className="text-xs">Precio Venta</Label>
                        <Input type="number" step="0.01" value={truckData.sale_price} onChange={(e) => setTruckData({ ...truckData, sale_price: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.sale_invoice && (
                      <div className="space-y-1">
                        <Label className="text-xs">Factura Venta</Label>
                        <UpperCaseInput value={truckData.sale_invoice} onChange={(e) => setTruckData({ ...truckData, sale_invoice: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.transport_invoice && (
                      <div className="space-y-1">
                        <Label className="text-xs">Factura Transporte</Label>
                        <UpperCaseInput value={truckData.transport_invoice} onChange={(e) => setTruckData({ ...truckData, transport_invoice: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                    {!selectedTruck?.guide_number && (
                      <div className="space-y-1">
                        <Label className="text-xs">N° Guía</Label>
                        <UpperCaseInput value={truckData.guide_number} onChange={(e) => setTruckData({ ...truckData, guide_number: e.target.value })} className="h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          
          <Button type="submit" className={`w-full text-white h-8 text-xs ${isModification ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
            {isModification ? 'Modificar Datos' : 'Agregar Datos'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TruckDataModal;
